import { useMemo } from 'react';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { AdvancedSearchByWeekIdResponse } from './types';
import { UseQueryResult } from '@tanstack/react-query';
import { useBaseMetricRequestBuilder } from './useBaseMetricRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { Field } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useAggregationBuilderWithMetrics';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';

interface UseMetricDataByWeekIdProps {
  requestId: string;
  startWeekId: number;
  endWeekId: number;
  indexName: string;
  fields: Field[];
  requestBuilder?: (builder: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder;
  responseParser?: (response: AxiosResponse<AdvancedSearchByWeekIdResponse<TResponseAdditionalValues[]>>) => any;
}

/**
 * Gets metrics for retail sales and units sold by week ID.
 */
export const useMetricDataByWeekId = <TResponseAdditionalValues, TParsedDatum>({
  startWeekId,
  endWeekId,
  requestId,
  fields,
  indexName,
  requestBuilder,
  responseParser
}: UseMetricDataByWeekIdProps): UseQueryResult<TParsedDatum[]> => {
  const entity = useAppSelector((state) => state.entityService.mainEntity);
  const requestIdWithEntityInfo = `${requestId}-${entity.type}-${entity.id}`;
  const requestBody = useBaseMetricRequestBuilder({
    endWeekId,
    startWeekId,
    fields,
    indexName,
    requestId: requestIdWithEntityInfo
  });

  const request = useMemo(() => {
    if (requestBuilder) {
      return requestBuilder(requestBody);
    } else {
      return requestBody;
    }
  }, [requestBuilder, requestBody]);

  const { data: response, ...rest } = useGenericAdvancedSearch<
    AdvancedSearchByWeekIdResponse<TResponseAdditionalValues[]>
  >({
    requestBody: [request.build()],
    requestId: requestIdWithEntityInfo,
    queryKeys: [request.build()]
  });

  const data: TParsedDatum[] = useMemo(() => {
    if (response && response.data && response.data[0]) {
      try {
        if (responseParser) {
          return responseParser(response);
        } else {
          const parsedData = response.data[0].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
            weekId: fieldId,
            ...additionalValues
          }));
          return parsedData;
        }
      } catch (err) {
        console.error('Failed to parse metric data by week ID', err);
        return [];
      }
    } else {
      return [];
    }
  }, [response, responseParser]);

  return {
    data,
    ...rest
  } as UseQueryResult<TParsedDatum[]>;
};
